import React, { useEffect } from 'react';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import FormLabel from '@material-ui/core/FormLabel';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import WarningIcon from '@material-ui/icons/Warning';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import TechnicalErrorAlert from '../../TechnicalErrorAlert/TechnicalErrorAlert';
import FileReader from '../FileReader/FileReader';
import { damageCodesState } from '../../HomePage/ContentTabs/ContentTabsSlice';
import { userInfoState } from '../../HomePage/HomeSlice';
import i18n from '../../../i18n';
import useStyles from './styles';

export default function AnomalyDetails(props) {
  const { anomaly, expanded, handleChange, validationErrors } = props;
  const classes = useStyles();
  const [formattedDamagesList, setFormattedDamagesList] = React.useState([]);
  const [attachments, setAttachments] = React.useState([]);
  const [anomalyDateErrorMessage, setAnomalyDateErrorMessage] =
    React.useState(null);
  const [damagePlaceErrorMessage, setDamagePlaceErrorMessage] =
    React.useState(null);
  const [damageCodeErrorMessage, setDamageCodeErrorMessage] =
    React.useState(null);
  const [openTechnicalErrorAlert, setOpenTechnicalErrorAlert] =
    React.useState(false);
  const [technicalErrorMessage, setTechnicalErrorMessage] =
    React.useState(null);
  const { t } = useTranslation();
  const lang = i18n.language;
  const [damageCodeList, setDamagesCodeList] = React.useState([]);
  const { englishDamageCodes } = useSelector(damageCodesState);
  const { germanDamageCodes } = useSelector(damageCodesState);
  const { frenchDamageCodes } = useSelector(damageCodesState);
  const user = useSelector(userInfoState);

  useEffect(() => {
    async function getAnomalyDetails() {
      setFormattedDamagesList(anomaly.damages);
      setAttachments(anomaly.attachments);
    }
    getAnomalyDetails();
  }, [anomaly]);

  useEffect(() => {
    if (validationErrors) {
      if (validationErrors.anomalyDate !== undefined) {
        setAnomalyDateErrorMessage(validationErrors.anomalyDate);
      }
      if (validationErrors.damagePlace !== undefined) {
        setDamagePlaceErrorMessage(validationErrors.damagePlace);
      }
      if (validationErrors.damageCode !== undefined) {
        setDamageCodeErrorMessage(validationErrors.damageCode);
      }
    } else {
      setAnomalyDateErrorMessage(null);
      setDamagePlaceErrorMessage(null);
      setDamageCodeErrorMessage(null);
    }
  }, [validationErrors]);

  const dateFormatter = (date) => {
    return new Intl.DateTimeFormat('en-GB').format(new Date(date));
  };

  const headingAnomalyFormatter = (anomalyDate, damagePlace) => {
    let headingAnomaly = t('Anomaly');
    if (anomalyDate !== null) {
      headingAnomaly = headingAnomaly
        .concat(' - ')
        .concat(dateFormatter(anomalyDate));
    }
    if (damagePlace !== null) {
      headingAnomaly = headingAnomaly.concat(' - ').concat(damagePlace);
    }
    return headingAnomaly;
  };

  function formatDamagesList() {
    const damagesList = [];
    formattedDamagesList.forEach((option) => {
      const formattedDamageCode = {};
      formattedDamageCode.code = option.code;
      if (
        damageCodeList.find((damage) => damage.code === option.code) !==
        undefined
      ) {
        formattedDamageCode.description = `${
          damageCodeList.find((damage) => damage.code === option.code)
            .description
        }`;
        damagesList.push(formattedDamageCode);
        setFormattedDamagesList(damagesList);
      }
    });
  }

  useEffect(() => {
    formatDamagesList();
  }, [damageCodeList]);

  useEffect(() => {
    if (lang === 'en') {
      setDamagesCodeList(englishDamageCodes);
    } else if (lang === 'fr') {
      setDamagesCodeList(frenchDamageCodes);
    } else if (lang === 'de') {
      setDamagesCodeList(germanDamageCodes);
    } else {
      setDamagesCodeList(englishDamageCodes);
    }
  }, [lang, englishDamageCodes, frenchDamageCodes, germanDamageCodes]);

  const damageFormatter = (code, description) => {
    return `${code} (${description})`;
  };

  const handleCloseTechnicalErrorAlert = () => {
    setOpenTechnicalErrorAlert(false);
    setTechnicalErrorMessage(null);
  };

  return (
    <Accordion
      className={classes.accordion}
      expanded={expanded}
      onChange={handleChange}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon className={classes.expandIcon} />}
      >
        <Typography className={classes.heading}>
          {headingAnomalyFormatter(anomaly.anomalyDate, anomaly.damagePlace)}
        </Typography>
        <Typography className={classes.secondaryHeading}>
          {t('Anomaly Details')}
        </Typography>
      </AccordionSummary>
      <AccordionDetails style={{ display: 'block' }}>
        <div className={classes.container}>
          <Grid container spacing={1} className={classes.gridMargin}>
            <Grid item xs={4}>
              <FormLabel className={classes.formLabel}>
                {t('Anomaly date')}
              </FormLabel>
            </Grid>
            <Grid item xs={4}>
              <FormLabel className={classes.formLabel}>
                {t('Source type')}
              </FormLabel>
            </Grid>
            <Grid item xs={4}>
              <FormLabel className={classes.formLabel}>
                {t('Anomaly Reporter')}
              </FormLabel>
            </Grid>
            <Grid item xs={4}>
              {anomalyDateErrorMessage !== null ? (
                <TextField
                  disabled
                  label=""
                  defaultValue=""
                  variant="outlined"
                  helperText={anomalyDateErrorMessage}
                  className={classes.warningMessage}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <WarningIcon className={classes.warningIcon} />
                      </InputAdornment>
                    ),
                  }}
                />
              ) : (
                <FormLabel className={classes.anomalyDetail}>
                  {dateFormatter(anomaly.anomalyDate)}
                </FormLabel>
              )}
            </Grid>
            <Grid item xs={4}>
              <FormLabel className={classes.anomalyDetail}>
                {t(anomaly.source)}
              </FormLabel>
            </Grid>
            <Grid item xs={4}>
              <FormLabel className={classes.anomalyDetail}>
                {anomaly.origin}
              </FormLabel>
            </Grid>
          </Grid>
          {user.hasAllRights ||
          user.hasEmilyDRights ||
          user.site === 'leuna' ? (
            <Grid container spacing={1} className={classes.gridMargin}>
              <Grid item xs={4}>
                <FormLabel className={classes.formLabel}>
                  {t('Place of damage')}
                </FormLabel>
              </Grid>
              <Grid item xs={4}>
                <FormLabel className={classes.formLabel}>
                  {t('Detailed Location')}
                </FormLabel>
              </Grid>
              <Grid item xs={4}>
                <FormLabel className={classes.formLabel}>
                  {t('Transport possible according to RID 4.3.2.4.3')}
                </FormLabel>
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={1} className={classes.gridMargin}>
              <Grid item xs={6}>
                <FormLabel className={classes.formLabel}>
                  {t('Place of damage')}
                </FormLabel>
              </Grid>
              <Grid item xs={6}>
                <FormLabel className={classes.formLabel}>
                  {t('Detailed Location')}
                </FormLabel>
              </Grid>
            </Grid>
          )}
          {user.hasAllRights ||
          user.hasEmilyDRights ||
          user.site === 'leuna' ? (
            <Grid container spacing={1} className={classes.gridMargin}>
              <Grid item xs={4}>
                {damagePlaceErrorMessage !== null ? (
                  <TextField
                    disabled
                    label=""
                    defaultValue=""
                    variant="outlined"
                    helperText={damagePlaceErrorMessage}
                    className={classes.warningMessage}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <WarningIcon className={classes.warningIcon} />
                        </InputAdornment>
                      ),
                    }}
                  />
                ) : (
                  <FormLabel className={classes.anomalyDetail}>
                    {anomaly.damagePlace}
                  </FormLabel>
                )}
              </Grid>
              <Grid item xs={4}>
                <FormLabel className={classes.anomalyDetail}>
                  {anomaly.detailedLocation ? anomaly.detailedLocation : '-'}
                </FormLabel>
              </Grid>
              <Grid item xs={4}>
                <FormLabel className={classes.anomalyDetail}>
                  {anomaly.transportPossible
                    ? t(anomaly.transportPossible)
                    : '-'}
                </FormLabel>
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={1} className={classes.gridMargin}>
              <Grid item xs={6}>
                {damagePlaceErrorMessage !== null ? (
                  <TextField
                    disabled
                    label=""
                    defaultValue=""
                    variant="outlined"
                    helperText={damagePlaceErrorMessage}
                    className={classes.warningMessage}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <WarningIcon className={classes.warningIcon} />
                        </InputAdornment>
                      ),
                    }}
                  />
                ) : (
                  <FormLabel className={classes.anomalyDetail}>
                    {anomaly.damagePlace}
                  </FormLabel>
                )}
              </Grid>
              <Grid item xs={6}>
                <FormLabel className={classes.anomalyDetail}>
                  {anomaly.detailedLocation ? anomaly.detailedLocation : '-'}
                </FormLabel>
              </Grid>
            </Grid>
          )}
          <Grid container spacing={1} className={classes.gridMargin}>
            <Grid item xs={6}>
              <FormLabel className={classes.formLabel}>
                {t('Label found on the wagon')}
              </FormLabel>
            </Grid>
            <Grid item xs={6}>
              <FormLabel className={classes.formLabel}>
                {t('Sample of labelling')}
              </FormLabel>
            </Grid>
            <Grid item xs={6}>
              <FormGroup row>
                {!anomaly.labelK &&
                  !anomaly.labelM &&
                  !anomaly.labelI &&
                  !anomaly.labelR1 &&
                  !anomaly.labelU &&
                  !anomaly.labelWagonDetached && (
                    <Typography className={classes.anomalyDetail}>-</Typography>
                  )}
                {anomaly.labelK && (
                  <FormControlLabel
                    disabled
                    control={
                      <Checkbox checked={anomaly.labelK} name="checkedLabelK" />
                    }
                    label={
                      <Typography className={classes.anomalyDetail}>
                        K
                      </Typography>
                    }
                  />
                )}
                {anomaly.labelM && (
                  <FormControlLabel
                    disabled
                    control={
                      <Checkbox checked={anomaly.labelM} name="checkedLabelM" />
                    }
                    label={
                      <Typography className={classes.anomalyDetail}>
                        M
                      </Typography>
                    }
                  />
                )}
                {anomaly.labelI && (
                  <FormControlLabel
                    disabled
                    control={
                      <Checkbox checked={anomaly.labelI} name="checkedLabelI" />
                    }
                    label={
                      <Typography className={classes.anomalyDetail}>
                        I
                      </Typography>
                    }
                  />
                )}
                {anomaly.labelR1 && (
                  <FormControlLabel
                    disabled
                    control={
                      <Checkbox
                        checked={anomaly.labelR1}
                        name="checkedLabelR1"
                      />
                    }
                    label={
                      <Typography className={classes.anomalyDetail}>
                        R1
                      </Typography>
                    }
                  />
                )}
                {anomaly.labelU && (
                  <FormControlLabel
                    disabled
                    control={
                      <Checkbox checked={anomaly.labelU} name="checkedLabelU" />
                    }
                    label={
                      <Typography className={classes.anomalyDetail}>
                        U
                      </Typography>
                    }
                  />
                )}
              </FormGroup>
            </Grid>
            <Grid item xs={6}>
              <FormLabel className={classes.anomalyDetail}>
                <FormGroup row>
                  {!anomaly.appliedLabelK &&
                    !anomaly.appliedLabelM &&
                    !anomaly.appliedLabelI &&
                    !anomaly.appliedLabelR1 &&
                    !anomaly.appliedLabelU &&
                    !anomaly.appliedLabelWagonDetached && (
                      <Typography className={classes.anomalyDetail}>
                        -
                      </Typography>
                    )}
                  {anomaly.appliedLabelK && (
                    <FormControlLabel
                      disabled
                      control={
                        <Checkbox
                          checked={anomaly.appliedLabelK}
                          name="checkedAppliedLabelK"
                        />
                      }
                      label={
                        <Typography className={classes.anomalyDetail}>
                          K
                        </Typography>
                      }
                    />
                  )}
                  {anomaly.appliedLabelM && (
                    <FormControlLabel
                      disabled
                      control={
                        <Checkbox
                          checked={anomaly.appliedLabelM}
                          name="checkedAppliedLabelM"
                        />
                      }
                      label={
                        <Typography className={classes.anomalyDetail}>
                          M
                        </Typography>
                      }
                    />
                  )}
                  {anomaly.appliedLabelI && (
                    <FormControlLabel
                      disabled
                      control={
                        <Checkbox
                          checked={anomaly.appliedLabelI}
                          name="checkedAppliedLabelI"
                        />
                      }
                      label={
                        <Typography className={classes.anomalyDetail}>
                          I
                        </Typography>
                      }
                    />
                  )}
                  {anomaly.appliedLabelR1 && (
                    <FormControlLabel
                      disabled
                      control={
                        <Checkbox
                          checked={anomaly.appliedLabelR1}
                          name="checkedAppliedLabelR1"
                        />
                      }
                      label={
                        <Typography className={classes.anomalyDetail}>
                          R1
                        </Typography>
                      }
                    />
                  )}
                  {anomaly.appliedLabelU && (
                    <FormControlLabel
                      disabled
                      control={
                        <Checkbox
                          checked={anomaly.appliedLabelU}
                          name="checkedAppliedLabelU"
                        />
                      }
                      label={
                        <Typography className={classes.anomalyDetail}>
                          U
                        </Typography>
                      }
                    />
                  )}
                  {anomaly.appliedLabelWagonDetached && (
                    <FormControlLabel
                      disabled
                      control={
                        <Checkbox
                          checked={anomaly.appliedLabelWagonDetached}
                          name="checkedAppliedLabelWagonDetached"
                        />
                      }
                      label={
                        <Typography className={classes.anomalyDetail}>
                          {t('Detached wagon')}
                        </Typography>
                      }
                    />
                  )}
                </FormGroup>
              </FormLabel>
            </Grid>
          </Grid>
          <Grid container spacing={1} className={classes.gridMargin}>
            <Grid item xs={12}>
              <FormLabel className={classes.formLabel}>
                {t('Damage codes')}
              </FormLabel>
            </Grid>
            <Grid item xs={12}>
              {damageCodeErrorMessage !== null ? (
                <TextField
                  disabled
                  label=""
                  defaultValue=""
                  variant="outlined"
                  helperText={damageCodeErrorMessage}
                  className={classes.warningMessageDamageCode}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <WarningIcon className={classes.warningIcon} />
                      </InputAdornment>
                    ),
                  }}
                />
              ) : (
                <List>
                  {formattedDamagesList.map((damage, damageIndex) => {
                    const damageId = `damage-${damageIndex}`;
                    return (
                      <ListItem key={damageId} className={classes.listItem}>
                        <ListItemText
                          disableTypography
                          primary={
                            <Typography className={classes.anomalyDetail}>
                              {damageFormatter(damage.code, damage.description)}
                            </Typography>
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Grid>
          </Grid>
          <Grid container spacing={1} className={classes.gridMargin}>
            <Grid item xs={12}>
              <FormLabel className={classes.formLabel}>
                {t('Description')}
              </FormLabel>
            </Grid>
            <Grid item xs={12}>
              <FormLabel className={classes.anomalyDetail}>
                {anomaly.description ? anomaly.description : '-'}
              </FormLabel>
            </Grid>
          </Grid>
          {attachments.length > 0 && (
            <FileReader
              attachments={attachments}
              anomalyIndex={anomaly.index}
              classes={classes}
              setOpenTechnicalErrorAlert={setOpenTechnicalErrorAlert}
              setTechnicalErrorMessage={setTechnicalErrorMessage}
            />
          )}
        </div>
      </AccordionDetails>
      <TechnicalErrorAlert
        openTechnicalErrorAlert={openTechnicalErrorAlert}
        technicalErrorMessage={technicalErrorMessage}
        handleCloseTechnicalErrorAlert={handleCloseTechnicalErrorAlert}
      />
    </Accordion>
  );
}
